// Import statements remain the same
import { Card, CardActions } from "@progress/kendo-react-layout";
import { ExternalDropZone, Upload, UploadFileInfo, UploadOnStatusChangeEvent } from "@progress/kendo-react-upload";
import { useFastaStore } from "@presentation/stores/FastaStore";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useInjection } from "@presentation/components/dependencyInjection"; //if you want to break the whole project with an error just at runtime(builds normally) impossible to guess just remove the /dependencyInjection
import { UploadFastaService } from "@application/useCases";
import React from "react";
import { FastaFile } from "@domain/schemas/BI";
import { useNotifications } from "@presentation/components/notifications";

/**
 * Define the interface for the event parameter in onStatusChange
 */
interface StatusChangeEvent {
  status: number;
  data?: { message: string };
  response?: { response: { data: { message: string } } };
}

interface UploadFastaProperties {
  onUploadSuccess?: (uploadedFasta: FastaFile) => void;
}

const uploadReference = React.createRef<Upload>();

// UploadCard component with TypeScript
const UploadFasta: React.FC<UploadFastaProperties> = ({ onUploadSuccess }) => {
  const uploadFastaFileService = useInjection(UploadFastaService);
  const { pushNotification } = useNotifications();

  const uploadVisibilitiy = useFastaStore((state) => state.isUploadVisible);
  const setUploadVisibilitiy = useFastaStore((state) => state.setUploadVisibilitiy);
  const uploadResults = useFastaStore((state) => state.uploadResults);
  const setUploadResults = useFastaStore((state) => state.setUploadResults);

  const onSaveUrl = async (
    files: UploadFileInfo[],
    _options: { formData: FormData; requestOptions: unknown },
    onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void,
  ): Promise<{ uid: string }> => {
    if (files.length > 0 && files[0].getRawFile) {
      const file = files[0].getRawFile();
      if (file) {
        // Call the upload service and pass the onProgress callback
        try {
          const response = await uploadFastaFileService.uploadFastaAsync(file, (progress) => {
            // Update the progress as the file uploads
            onProgress(files[0].uid, {
              lengthComputable: true,
              loaded: progress,
              total: 100,
            } as ProgressEvent<EventTarget>);
          });

          // Once the upload is complete
          if (onUploadSuccess) {
            pushNotification("The fasta file was successfuly uploaded.", "success", 10_000);
            onUploadSuccess(response as unknown as FastaFile);
          }
        } catch (error: any) {
          pushNotification(error.response.data.message || "An error occurred while uploading the file.", "error", 10_000);
        }
      } else {
        console.log(new Error("File could not be retrieved from UploadFileInfo."));
      }
    } else {
      console.log(new Error("No files to upload."));
    }
    return { uid: "" };
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    const statusEvent = event as unknown as StatusChangeEvent;

    if (statusEvent.status === 200 && statusEvent.data) {
      setUploadResults(statusEvent.data.message);
      setUploadVisibilitiy(true);
    } else if (statusEvent.response) {
      setUploadResults(statusEvent.response.response.data.message);
      alert(uploadResults);
    }
  };

  const toggleDialog = () => {
    setUploadVisibilitiy(false);
    location.assign("/");
  };

  return (
    <div>
      {uploadVisibilitiy && (
        <Dialog title="Upload Results" onClose={toggleDialog} width={"100%"} height={"100%"}>
          <p>Results of upload:</p>
          <p>{uploadResults}</p>

          <DialogActionsBar>
            <Button onClick={toggleDialog}>OK</Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Card style={{ height: "100%" }}>
        <CardActions>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              fontSize: "14px",
              gridTemplateRows: "auto auto auto",
            }}
          >
            <ul>
              <li>Needs to have a .fasta or .bgsfasta extension.</li>
              <li>Max file size is 2GB.</li>
              <li>Only one file can be uploaded at a time.</li>
            </ul>
            <p>Note: Loading can take a second. </p>
            <br></br>
            <div>
              <ExternalDropZone style={{ minWidth: "35vh" }} uploadRef={uploadReference} />
              <Upload
                ref={uploadReference}
                batch={false}
                multiple={false}
                withCredentials={false}
                defaultFiles={[]}
                restrictions={{
                  allowedExtensions: [".fasta", ".bgsfasta"],
                  maxFileSize: 2000 * 1024 * 1024,
                }}
                accept=".fasta,.bgsfasta"
                saveUrl={onSaveUrl}
                onStatusChange={(event) => onStatusChange(event)}
                saveField="files"
              />
            </div>
          </div>
        </CardActions>
      </Card>
    </div>
  );
};

export default UploadFasta;
